"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  KeyRound,
  Search,
  SendHorizonal,
} from "lucide-react";
import { ApiDocEndpoint, ApiDocGroup } from "@/lib/api-docs";

const methodStyles: Record<string, string> = {
  GET: "bg-[#E6F7EE] text-[#16A34A]",
  POST: "bg-[#E8F0FF] text-[#2563EB]",
  PUT: "bg-[#fff4e5] text-[#d97706] dark:bg-[#d97706]/16 dark:text-[#f9c66b]",
  DELETE: "bg-[#ffe9e9] text-[#e11d48] dark:bg-[#e11d48]/16 dark:text-[#fda4af]",
};

function getMethodStyle(method: string) {
  return (
    methodStyles[method.toUpperCase()] ||
    "bg-[#eef2ff] text-[#4f46e5] dark:bg-[#4f46e5]/16 dark:text-[#c7d2fe]"
  );
}

function getStatusStyle(status: number) {
  if (status >= 200 && status < 300) {
    return "border border-[#CDEDD9] bg-[#ECFDF3] text-[#15803D]";
  }

  return "border border-[#FFD6DE] bg-[#FFF4F6] text-[#BE123C]";
}

function getResponsePayload(result: { data: unknown; error?: string } | null) {
  if (!result) return "";
  if (result.error) return result.error;
  return JSON.stringify(result.data, null, 2);
}

function getEndpointPath(url?: string) {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function getParamTypeLabel(type?: string) {
  switch (type) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "select":
      return "enum";
    default:
      return "text";
  }
}

function ParamSelect({
  value,
  options,
  placeholder,
  required,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  required?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <input
        tabIndex={-1}
        value={value}
        onChange={() => {}}
        required={required}
        readOnly
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      />
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-10 w-full items-center justify-between rounded-[14px] border bg-white px-3 text-left text-sm outline-none transition-all ${
          open
            ? "border-[#2563EB] shadow-[0_0_0_4px_rgba(37,99,235,0.08)]"
            : "border-[#d7e1f1] hover:border-[#bfd0ea]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedOption ? "text-[#111827]" : "text-[#9CA3AF]"}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#7b8aa3] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-[16px] border border-[#d7e1f1] bg-white shadow-[0_18px_40px_rgba(15,23,42,0.10)]">
          <div className="p-2">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-sm transition-colors ${
                    isSelected
                      ? "bg-[#edf3ff] text-[#1d4ed8]"
                      : "text-[#334155] hover:bg-[#f7faff]"
                  }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span>{option.label}</span>
                  {isSelected ? (
                    <span className="text-[11px] font-medium uppercase tracking-[0.14em]">
                      已选
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getUniqueApiParams(apiParams?: ApiDocEndpoint["api_params"]) {
  if (!apiParams) return [];

  const seen = new Set<string>();
  return apiParams.filter((param) => {
    const key =
      typeof param?.name === "string" && param.name.trim()
        ? param.name.trim()
        : JSON.stringify(param);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function getFilledRequiredCount(
  params: Record<string, string | number | boolean>,
  apiParams: ReturnType<typeof getUniqueApiParams>,
) {
  return apiParams.filter((param) => {
    if (!param.required) return false;
    const value = params[param.name];
    return value !== undefined && value !== "";
  }).length;
}

export default function PlaygroundClient({ apiGroups }: { apiGroups: ApiDocGroup[] }) {
  const [activeEndpoint, setActiveEndpoint] = useState<ApiDocEndpoint | null>(
    () => apiGroups[0]?.endpoints[0] ?? null,
  );
  const [apiKey, setApiKey] = useState("");
  const [endpointQuery, setEndpointQuery] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [params, setParams] = useState<Record<string, string | number | boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<{
    data: unknown;
    status: number;
    timeMs: number;
    error?: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const uniqueApiParams = getUniqueApiParams(activeEndpoint?.api_params);
  const filledRequiredCount = useMemo(
    () => getFilledRequiredCount(params, uniqueApiParams),
    [params, uniqueApiParams],
  );
  const requiredCount = uniqueApiParams.filter((param) => param.required).length;
  const endpointPath = getEndpointPath(activeEndpoint?.api_url);
  const normalizedEndpointQuery = endpointQuery.trim().toLowerCase();
  const filteredApiGroups = useMemo(() => {
    if (!normalizedEndpointQuery) {
      return apiGroups;
    }

    return apiGroups
      .map((group) => ({
        ...group,
        endpoints: group.endpoints.filter((endpoint) => {
          const searchText = [
            group.group,
            endpoint.title,
            endpoint.api_method,
            getEndpointPath(endpoint.api_url),
          ]
            .join(" ")
            .toLowerCase();
          return searchText.includes(normalizedEndpointQuery);
        }),
      }))
      .filter((group) => group.endpoints.length > 0);
  }, [apiGroups, normalizedEndpointQuery]);
  const visibleEndpointCount = filteredApiGroups.reduce(
    (sum, group) => sum + group.endpoints.length,
    0,
  );

  useEffect(() => {
    if (apiGroups.length > 0 && apiGroups[0].endpoints.length > 0 && !activeEndpoint) {
      handleSelectEndpoint(apiGroups[0].endpoints[0]);
    }
  }, [apiGroups, activeEndpoint]);

  useEffect(() => {
    setCollapsedGroups((prev) => {
      const next = { ...prev };
      apiGroups.forEach((group) => {
        if (next[group.group] === undefined) {
          next[group.group] = false;
        }
      });
      return next;
    });
  }, [apiGroups]);

  const handleSelectEndpoint = (endpoint: ApiDocEndpoint) => {
    setActiveEndpoint(endpoint);
    setResponse(null);
    setParams({});
  };

  const handleParamChange = (name: string, value: string | number | boolean) => {
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeEndpoint || !activeEndpoint.api_url) return;

    setIsLoading(true);
    setResponse(null);

    const startTime = performance.now();
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (apiKey) {
        headers["Authorization"] = `Bearer ${apiKey}`;
      }

      const processedParams = { ...params };
      uniqueApiParams.forEach((param) => {
        const currentValue = processedParams[param.name];
        if (param.type === "number" && currentValue !== undefined && currentValue !== "") {
          processedParams[param.name] = Number(processedParams[param.name]);
        } else if (param.type === "boolean" && currentValue !== undefined && currentValue !== "") {
          processedParams[param.name] =
            processedParams[param.name] === "true" || processedParams[param.name] === true;
        }
      });

      const fetchOptions: RequestInit = {
        method: activeEndpoint.api_method.toUpperCase(),
        headers,
      };

      let finalUrl = activeEndpoint.api_url;
      if (fetchOptions.method === "GET" || fetchOptions.method === "HEAD") {
        const urlObj = new URL(finalUrl);
        Object.entries(processedParams).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            urlObj.searchParams.append(key, value.toString());
          }
        });
        finalUrl = urlObj.toString();
      } else {
        fetchOptions.body = JSON.stringify(processedParams);
      }

      const res = await fetch(finalUrl, fetchOptions);
      const endTime = performance.now();
      const timeMs = Math.round(endTime - startTime);

      let data;
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        data = await res.text();
      }

      const errorMsg = !res.ok
        ? typeof data === "string"
          ? data
          : JSON.stringify(data)
        : undefined;

      const newResponse = {
        data: res.ok ? data : null,
        status: res.status,
        timeMs,
        error: errorMsg,
      };

      setResponse(newResponse);
    } catch (err: unknown) {
      const endTime = performance.now();
      const timeMs = Math.round(endTime - startTime);
      const errorMsg =
        err instanceof Error ? err.message : "请求失败，可能是跨域问题或网络连接中断";

      const errorResponse = {
        data: null,
        status: 0,
        timeMs,
        error: errorMsg,
      };

      setResponse(errorResponse);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-[linear-gradient(180deg,#f6f9ff_0%,#f8fbff_28%,#eef4fb_100%)] px-4 py-4 md:px-6 md:py-5">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-[-8%] h-80 w-80 rounded-full bg-[#335AFF]/8 blur-3xl" />
        <div className="absolute right-[-8%] top-[8%] h-[24rem] w-[24rem] rounded-full bg-[#7E9BFF]/10 blur-3xl" />
        <div className="absolute bottom-[-18%] left-[12%] h-[18rem] w-[18rem] rounded-full bg-[#BFD0FF]/14 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.72)_0%,rgba(255,255,255,0.18)_24%,rgba(255,255,255,0.32)_100%)]" />
        <div className="absolute inset-0 opacity-[0.24] [background-image:linear-gradient(rgba(148,163,184,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.16)_1px,transparent_1px)] [background-size:24px_24px]" />
      </div>

      <div className="relative mx-auto flex h-full min-h-0 w-full max-w-[1680px] flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[28px] border border-[#d8e4f2] bg-[linear-gradient(180deg,rgba(255,255,255,0.82)_0%,rgba(248,251,255,0.78)_100%)] shadow-[0_26px_72px_rgba(15,23,42,0.08)] backdrop-blur-[18px] xl:grid xl:grid-cols-[320px_minmax(420px,560px)_minmax(0,1fr)]">
          <aside className="flex min-h-[260px] flex-col border-b border-[#e4ebf6] bg-[linear-gradient(180deg,rgba(251,253,255,0.96)_0%,rgba(246,249,255,0.92)_100%)] xl:min-h-0 xl:border-b-0 xl:border-r">
            <div className="flex flex-col gap-4 border-b border-[#e4ebf6] px-5 py-5 xl:min-h-[168px] xl:justify-between">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7b8aa3]">
                    <BookOpen className="h-3.5 w-3.5" />
                    API 目录
                  </div>
                  <div className="text-xl font-semibold tracking-[-0.02em] text-[#0f172a]">浏览全部接口</div>
                </div>
                <span className="inline-flex h-8 shrink-0 items-center self-start rounded-full border border-[#dbe6f7] bg-white px-3 text-[11px] font-medium text-[#607089]">
                  可见接口 {visibleEndpointCount}
                </span>
              </div>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8ca0bd]" />
                <input
                  type="text"
                  value={endpointQuery}
                  onChange={(e) => setEndpointQuery(e.target.value)}
                  placeholder="搜索接口名称、路径或方法"
                  className="h-10 w-full rounded-[14px] border border-[#d7e1f1] bg-white pl-10 pr-3 text-sm text-[#111827] outline-none transition-all placeholder:text-[#9CA3AF] focus:border-[#2563EB] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.08)]"
                />
              </div>
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto px-4 py-3">
              {filteredApiGroups.length === 0 ? (
                <div className="rounded-[18px] border border-dashed border-[#d9e4f4] bg-white/70 px-4 py-5 text-sm leading-6 text-[#6b7b92]">
                  未找到匹配接口。
                </div>
              ) : (
                filteredApiGroups.map((group, idx) => {
                  const isCollapsed = normalizedEndpointQuery ? false : collapsedGroups[group.group];

                  return (
                <div key={group.group} className={idx === 0 ? "" : "mt-4"}>
                  <div className="overflow-hidden rounded-[20px] border border-[#e4ebf6] bg-white/72">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.group)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#f7faff]"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#dce7fb] bg-[#edf3ff] text-[#4c6fff]">
                          {isCollapsed ? (
                            <ChevronRight className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-semibold text-[#25364d]">
                            {group.group}
                          </div>
                          <div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.18em] text-[#93a1b7]">
                            {group.endpoints.length} 个接口
                          </div>
                        </div>
                      </div>
                      <span className="inline-flex h-7 shrink-0 items-center rounded-full border border-[#dce8fb] bg-white px-2.5 text-[11px] font-medium text-[#5b77c8]">
                        {group.endpoints.length}
                      </span>
                    </button>

                    {!isCollapsed ? (
                      <div className="border-t border-[#edf2fb] px-2 py-2">
                        <ul className="space-y-1">
                          {group.endpoints.map((endpoint) => {
                            const isActive = activeEndpoint?.id === endpoint.id;
                            return (
                              <li key={endpoint.id}>
                                <button
                                  type="button"
                                  onClick={() => handleSelectEndpoint(endpoint)}
                                  className={`group relative w-full rounded-[16px] px-3 py-3 text-left transition-all duration-200 ${
                                    isActive
                                      ? "bg-[linear-gradient(180deg,#eef4ff_0%,#f8fbff_100%)] text-[#1736a2]"
                                      : "text-[#334155] hover:bg-[#f8fbff]"
                                  }`}
                                >
                                  <span
                                    className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full ${
                                      isActive ? "bg-[#335AFF]" : "bg-transparent"
                                    }`}
                                  />
                                  <div className="flex items-start justify-between gap-3 pl-2">
                                    <div className="min-w-0">
                                      <div
                                        className={`truncate text-[13px] leading-5 ${
                                          isActive ? "font-semibold" : "font-medium"
                                        }`}
                                      >
                                        {endpoint.title}
                                      </div>
                                      <div className="mt-1 truncate font-mono text-[10px] text-[#6d809a]">
                                        {getEndpointPath(endpoint.api_url)}
                                      </div>
                                    </div>
                                    <span
                                      className={`mt-0.5 inline-flex h-5 shrink-0 items-center rounded-[999px] px-2.5 text-[11px] font-semibold ${getMethodStyle(
                                        endpoint.api_method.toUpperCase(),
                                      )}`}
                                    >
                                      {endpoint.api_method.toUpperCase()}
                                    </span>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                  );
                })
              )}
            </div>
          </aside>

          <section className="flex min-h-[520px] min-w-0 flex-col border-b border-[#e4ebf6] xl:min-h-0 xl:border-b-0 xl:border-r">
            {activeEndpoint ? (
              <>
                <div className="flex flex-col gap-4 border-b border-[#e4ebf6] px-6 py-5 xl:min-h-[168px] xl:justify-between">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex h-7 items-center rounded-full px-3 text-[11px] font-semibold ${getMethodStyle(
                        activeEndpoint.api_method.toUpperCase(),
                      )}`}
                    >
                      {activeEndpoint.api_method.toUpperCase()}
                    </span>
                    <span className="inline-flex h-7 items-center rounded-full border border-[#dbe6f7] bg-white px-3 text-[11px] font-medium text-[#64748b]">
                      分组: {activeEndpoint.group}
                    </span>
                    <span className="inline-flex h-7 items-center rounded-full border border-[#dbe6f7] bg-white px-3 text-[11px] font-medium text-[#64748b]">
                      已填 {filledRequiredCount}/{requiredCount || 0} 个必填参数
                    </span>
                  </div>
                    <Link
                      href={activeEndpoint.href}
                      className="inline-flex items-center gap-1 self-start pt-0.5 text-sm font-medium text-[#335AFF] transition-colors hover:text-[#2248da]"
                    >
                      查看文档
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-[#0f172a]">
                      {activeEndpoint.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-[#5f7189]">
                      <span className="rounded-[12px] border border-[#dce7f6] bg-[#f7faff] px-3 py-1.5 font-mono text-[12px] text-[#36506d]">
                        {endpointPath || activeEndpoint.api_url}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#335AFF]">
                        <KeyRound className="h-3.5 w-3.5" />
                        Bearer Token
                      </span>
                    </div>
                  </div>
                </div>

                <form id="api-form" onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <section className="border-b border-[#edf2fb] px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#111827]">
                          鉴权
                        </label>
                        <p className="mt-1 text-xs leading-5 text-[#6B7280]">
                          输入 API Key。
                        </p>
                      </div>
                      <span className="hidden rounded-full border border-[#dbe6f7] bg-[#f7faff] px-3 py-1 text-[11px] font-medium text-[#607089] md:inline-flex">
                        自动补全 `Bearer `
                      </span>
                    </div>

                    <div className="relative mt-3">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                      <input
                        type={showApiKey ? "text" : "password"}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="请输入你的 API Key"
                        className="h-10 w-full rounded-[14px] border border-[#d7e1f1] bg-white pl-10 pr-11 text-sm text-[#111827] outline-none transition-all placeholder:text-[#9CA3AF] focus:border-[#2563EB] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.10)]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#7b8aa3] transition-colors hover:text-[#335AFF]"
                        title={showApiKey ? "隐藏 API Key" : "显示 API Key"}
                      >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </section>

                  <section className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 py-3">
                    <div className="flex items-center justify-between gap-4 border-b border-[#edf2fb] pb-2.5">
                      <div>
                        <div className="text-sm font-semibold text-[#111827]">请求参数</div>
                        <p className="mt-0.5 text-xs leading-5 text-[#6B7280]">
                          填写参数。
                        </p>
                      </div>
                      <span className="rounded-full border border-[#dbe6f7] bg-white px-3 py-1 text-[11px] font-medium text-[#607089]">
                        共 {uniqueApiParams.length} 项
                      </span>
                    </div>

                    {uniqueApiParams.length === 0 ? (
                      <div className="mt-3 rounded-[18px] border border-dashed border-[#dbe6f7] bg-white/70 px-4 py-3.5 text-sm leading-7 text-[#64748b]">
                        此接口无需额外参数。
                      </div>
                    ) : (
                      <div className="min-h-0 flex-1 overflow-hidden pt-0.5">
                        <div className="custom-scrollbar h-full overflow-y-auto pr-2">
                          {uniqueApiParams.map((param, idx) => (
                            <div
                              key={param.name || idx}
                              className={`grid gap-3 py-3 ${
                                idx === 0 ? "" : "border-t border-[#e4ebf5]"
                              }`}
                            >
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-5">
                                <label className="text-sm font-semibold text-[#111827]">
                                  {param.label || param.name}
                                </label>
                                <span className="inline-flex h-5 items-center rounded-full border border-[#e2e8f3] bg-white px-2.5 text-[11px] font-medium uppercase text-[#64748b]">
                                  {getParamTypeLabel(param.type)}
                                </span>
                                {param.required ? (
                                  <span className="inline-flex h-5 items-center rounded-full border border-[#ffd7df] bg-[#fff4f6] px-2.5 text-[11px] font-medium text-[#ef4444]">
                                    必填
                                  </span>
                                ) : (
                                  <span className="inline-flex h-5 items-center rounded-full border border-[#dbe6f7] bg-[#f8fbff] px-2.5 text-[11px] font-medium text-[#607089]">
                                    可选
                                  </span>
                                )}
                                {param.placeholder ? (
                                  <span className="min-w-0 text-[12px] text-[#7b8aa3]">
                                    {param.placeholder}
                                  </span>
                                ) : null}
                              </div>

                              {param.type === "select" || param.type === "boolean" ? (
                                <ParamSelect
                                  value={
                                    params[param.name] === undefined
                                      ? ""
                                      : params[param.name].toString()
                                  }
                                  onChange={(nextValue) =>
                                    handleParamChange(
                                      param.name,
                                      param.type === "boolean"
                                        ? nextValue === "true"
                                        : nextValue,
                                    )
                                  }
                                  required={param.required}
                                  placeholder={`请选择 ${param.label}`}
                                  options={
                                    param.type === "boolean"
                                      ? [
                                          { value: "true", label: "是" },
                                          { value: "false", label: "否" },
                                        ]
                                      : (param.options || []).map((opt: string) => ({
                                          value: opt,
                                          label: opt,
                                        }))
                                  }
                                />
                              ) : (
                                <input
                                  type={param.type === "number" ? "number" : "text"}
                                  value={(params[param.name] as string | number) || ""}
                                  onChange={(e) => handleParamChange(param.name, e.target.value)}
                                  placeholder={param.placeholder || `请输入 ${param.label}`}
                                  required={param.required}
                                  className="h-10 w-full rounded-[14px] border border-[#d7e1f1] bg-white px-3 text-sm text-[#111827] outline-none transition-all placeholder:text-[#9CA3AF] focus:border-[#2563EB] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.10)]"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                </form>

                <div className="border-t border-[#e4ebf6] bg-white/56 px-6 py-3.5">
                  <div className="flex items-center justify-between gap-4 py-1">
                    <p className="max-w-[22rem] text-xs leading-5 text-[#7b8aa3]">
                      发送后在右侧查看返回结果。
                    </p>
                    <button
                      type="submit"
                      form="api-form"
                      disabled={isLoading}
                      className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-[14px] bg-[linear-gradient(135deg,#335AFF_0%,#234AE6_100%)] px-5 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(37,99,235,0.18)] transition-all duration-300 hover:translate-y-[-1px] hover:shadow-[0_22px_48px_rgba(37,99,235,0.24)] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isLoading ? (
                        <>
                          <svg
                            className="h-4 w-4 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>请求中...</span>
                        </>
                      ) : (
                        <>
                          <SendHorizonal className="h-4 w-4" />
                          <span>发送请求</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-[#7b8aa3]">
                请选择接口。
              </div>
            )}
          </section>

          <section className="flex min-h-[520px] min-w-0 flex-1 flex-col overflow-hidden xl:min-h-0">
            <div className="flex flex-col gap-4 border-b border-[#e4ebf6] px-6 py-5 xl:min-h-[168px] xl:justify-between">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7b8aa3]">
                    响应结果
                  </div>
                  <div className="text-xl font-semibold tracking-[-0.02em] text-[#0f172a]">最新响应结果</div>
                  <p className="text-sm leading-6 text-[#607089]">查看状态码、耗时和响应数据。</p>
                </div>

                <div className="flex items-center gap-2 self-start pt-0.5">
                  {response ? (
                    <>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${getStatusStyle(
                          response.status,
                        )}`}
                      >
                        {response.status === 0 ? "Error" : response.status}
                      </span>
                      <span className="rounded-full border border-[#d6e4ff] bg-[#edf4ff] px-2.5 py-1 text-[11px] font-medium text-[#3d66f5]">
                        {response.timeMs} ms
                      </span>
                    </>
                  ) : (
                    <span className="rounded-full border border-[#e2e8f3] bg-white px-2.5 py-1 text-[11px] font-medium text-[#8a9ab0]">
                      等待请求
                    </span>
                  )}

                  {(response?.data !== null || response?.error) && (
                    <button
                      type="button"
                      onClick={() => handleCopy(getResponsePayload(response))}
                      className="inline-flex h-9 items-center gap-2 rounded-[12px] border border-[#dbe6f7] bg-white px-3 text-sm text-[#607089] transition-colors hover:border-[#c7d8f6] hover:text-[#335AFF]"
                      title="复制结果"
                    >
                      {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      复制
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="custom-scrollbar flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {!response && !isLoading && (
                <div className="flex min-h-full items-center justify-center rounded-[24px] border border-dashed border-[#dbe6f7] bg-[linear-gradient(180deg,rgba(255,255,255,0.82)_0%,rgba(246,249,255,0.82)_100%)] px-6 py-14 text-center">
                  <div className="max-w-md">
                    <div className="mx-auto inline-flex rounded-full border border-[#d7e4ff] bg-[#edf3ff] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#335AFF]">
                      就绪
                    </div>
                    <h3 className="mt-4 text-[22px] font-semibold tracking-[-0.02em] text-[#0f172a]">
                      响应结果
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-[#607089]">
                      发送请求后显示响应结果。
                    </p>
                  </div>
                </div>
              )}

              {isLoading && !response && (
                <div className="flex min-h-full items-center justify-center rounded-[24px] border border-[#e5edf8] bg-[linear-gradient(180deg,rgba(250,252,255,0.92)_0%,rgba(244,248,255,0.92)_100%)] px-6 py-14">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#d7e4ff] bg-white shadow-[0_10px_30px_rgba(96,165,250,0.08)]">
                      <svg
                        className="h-6 w-6 animate-spin text-[#4f7cff]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <div className="text-sm font-medium text-[#0f172a]">请求中</div>
                    <div className="text-xs text-[#6f8098]">等待响应...</div>
                  </div>
                </div>
              )}

              {response &&
                (response.error ? (
                  <div className="overflow-hidden rounded-[24px] border border-[#ffd7df] bg-[#fff8fa]">
                    <div className="border-b border-[#ffe1e6] px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#be365d]">
                      错误信息
                    </div>
                    <div className="whitespace-pre-wrap px-5 py-5 font-mono text-sm leading-7 text-[#be365d]">
                      {response.error}
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-[24px] border border-[#18263d] bg-[#0f172a] shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
                    <div className="flex items-center justify-between border-b border-white/10 bg-[linear-gradient(180deg,rgba(20,31,52,0.96)_0%,rgba(15,23,42,1)_100%)] px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full bg-[#fb7185]" />
                          <span className="h-2.5 w-2.5 rounded-full bg-[#fbbf24]" />
                          <span className="h-2.5 w-2.5 rounded-full bg-[#34d399]" />
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9fb6d8]">
                          响应数据
                        </div>
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium text-[#d8e6ff]">
                        {endpointPath || "payload"}
                      </div>
                    </div>
                    <pre className="overflow-x-auto whitespace-pre-wrap break-all px-5 py-5 font-mono text-sm leading-7 text-[#dbeafe]">
                      {JSON.stringify(response.data, null, 2)}
                    </pre>
                  </div>
                ))}
            </div>
          </section>
        </div>
      </div>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
      `}</style>
    </div>
  );
}
